import { Mail } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { callAppsScript } from '@/lib/agents/sheets-tools'
import { handleUnauthorized } from '@/lib/api-error-handler'
import { openGoogleAuthDialog } from '@/lib/auth-dialog'
import { API_URL, BACKEND_URL } from '@/lib/config'
import { usePlatform } from '@/lib/platform'
import { getAuthToken, supabase } from '@/lib/supabase'

export default function LoginPage() {
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false)
  const [isSignUp, setIsSignUp] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const platform = usePlatform()
  const autoLoginAttempted = useRef(false)

  const claimSignupBonus = async (source: string) => {
    try {
      const token = await getAuthToken()
      if (!token) {
        console.warn('No auth token available for bonus claim')
        return
      }

      const response = await fetch(`${BACKEND_URL}/api/auth/grant-bonus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({ source }),
      })

      if (!response.ok) {
        if (response.status === 401) {
          await handleUnauthorized(response, 'Claim signup bonus')
        }
        // Don't throw - bonus failure shouldn't block login
        return
      }

      console.log('Signup bonus claimed')
    } catch (error) {
      console.error('Failed to claim signup bonus:', error)
      // Don't throw - bonus failure shouldn't block login
    }
  }

  const handleGoogleSignIn = async () => {
    if (platform === 'sheets') {
      setIsAuthenticating(true)
      setError(null)
      try {
        // Get Google Identity Token from Apps Script (cryptographically verified by Google)
        const result = await callAppsScript<{
          success: boolean
          idToken: string
          email: string
        }>('getGoogleIdToken')

        if (!result.success || !result.idToken) {
          throw new Error(
            'Failed to get Google identity token from Google Sheets'
          )
        }

        // Authenticate with API layer (Express/Vercel) using the verified Google ID token
        // This is still needed for Google Sheets because we need to verify the Google ID token
        const response = await fetch(
          `${API_URL}/api/auth?provider=google&action=sheets`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'ngrok-skip-browser-warning': 'true',
            },
            body: JSON.stringify({ idToken: result.idToken }),
          }
        )

        const data = await response.json()

        if (!response.ok) {
          throw new Error(
            data.detail || data.error || 'Failed to authenticate with Google'
          )
        }

        const accessToken = data.access_token || data.token
        const refreshToken = data.refresh_token || null

        if (!accessToken || !refreshToken) {
          throw new Error('No tokens received from server')
        }

        // Let Supabase manage the session
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        })

        if (sessionError) {
          throw sessionError
        }

        // Claim signup bonus
        await claimSignupBonus('google-sheets')
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to sign in with Google'
        console.error('Sign in error:', errorMessage)
        setError(errorMessage)
      } finally {
        setIsAuthenticating(false)
      }
      return
    }

    setIsAuthenticating(true)
    setError(null)
    try {
      const result = await openGoogleAuthDialog()

      if (result) {
        const accessToken = result.token
        const refreshToken = result.refreshToken

        if (!accessToken || !refreshToken) {
          throw new Error('Invalid auth response - missing tokens')
        }

        // Let Supabase manage the session
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        })

        if (sessionError) {
          throw sessionError
        }

        // Claim signup bonus
        await claimSignupBonus('google')
      } else {
        const errorMsg = 'Authentication was cancelled by user'
        console.warn(errorMsg)
        setError(errorMsg)
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to sign in with Google'
      console.error('Sign in error:', errorMessage)
      setError(errorMessage)
    } finally {
      setIsAuthenticating(false)
    }
  }

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsAuthenticating(true)
    setError(null)
    setSuccessMessage(null)

    try {
      if (isSignUp) {
        const { data, error } = await supabase.auth.signUp({ email, password })

        if (error) {
          throw error
        }

        // Check if email confirmation is required
        if (data.user && !data.session) {
          setSuccessMessage(
            'Account created! Please check your email for a confirmation link.'
          )
          return
        }

        // If session exists, user is logged in (confirmation not required)
        if (data.session) {
          setSuccessMessage('Account created successfully!')
          await claimSignupBonus('email')
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        })

        if (error) {
          throw error
        }

        // Supabase automatically manages the session
        // No need to manually store tokens or call setSession

        // Claim signup bonus (backend will prevent duplicates)
        await claimSignupBonus('email')
      }
    } catch (error) {
      console.error('Email auth error:', error)
      const errorMessage =
        error instanceof Error ? error.message : 'Authentication failed'
      setError(errorMessage)
    } finally {
      setIsAuthenticating(false)
    }
  }

  // Auto-login on Google Sheets platform using existing Google OAuth authorization
  useEffect(() => {
    if (platform !== 'sheets' || autoLoginAttempted.current) {
      return
    }
    autoLoginAttempted.current = true
    handleGoogleSignIn()
  }, [platform])

  // Show loading state during auto-login on Sheets
  if (platform === 'sheets' && isAuthenticating && !error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-orange-50/30 to-amber-50/50">
        <div className="text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-[#FF6B00] rounded-2xl blur-xl opacity-20"></div>
              <div className="relative bg-white rounded-2xl p-3 shadow-lg border border-slate-200/60">
                <img
                  src="/assets/logo-80.png"
                  alt="Pipedreamer"
                  className="w-16 h-16 object-contain"
                />
              </div>
            </div>
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B00] mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Signing you in...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 via-orange-50/30 to-amber-50/50">
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-[#FF6B00] rounded-2xl blur-xl opacity-20"></div>
              <div className="relative bg-white rounded-2xl p-3 shadow-lg border border-slate-200/60">
                <img
                  src="/assets/logo-80.png"
                  alt="Pipedreamer"
                  className="w-16 h-16 object-contain"
                />
              </div>
            </div>
          </div>
          <h1 className="text-4xl font-bold text-[#FF6B00] mb-3 tracking-tight">
            Pipedreamer
          </h1>
          <p className="text-slate-600 text-lg font-medium">
            Your Spreadsheets, Done in Seconds
          </p>
        </div>

        {/* Card */}
        <div className="p-8 space-y-6">
          <div className="space-y-5">
            {/* Google OAuth - Official Google Sign In Button */}
            <button
              onClick={handleGoogleSignIn}
              disabled={isAuthenticating}
              className="w-full h-11 bg-white border border-[#DADCE0] rounded-md px-4 flex items-center justify-center gap-3 hover:bg-[#F8F9FA] hover:shadow-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              <span className="text-[#3C4043] text-sm font-medium">
                {isAuthenticating ? 'Signing in...' : 'Sign in with Google'}
              </span>
            </button>

            {/* OR */}
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white/80 text-slate-500 font-medium">
                  Or continue with email
                </span>
              </div>
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleEmailAuth} className="space-y-4">
              <div className="space-y-3">
                <div className="relative">
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isAuthenticating}
                    required
                    className="h-12 text-base border-slate-300 focus:border-[#FF6B00] focus:ring-[#FF6B00]"
                  />
                </div>
                <div className="relative">
                  <Input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isAuthenticating}
                    required
                    minLength={6}
                    className="h-12 text-base border-slate-300 focus:border-[#FF6B00] focus:ring-[#FF6B00]"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isAuthenticating || !email || !password}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white shadow-md hover:shadow-lg transition-all duration-200 h-12 text-base font-semibold"
                size="lg"
              >
                <Mail className="w-5 h-5 mr-2" />
                {isAuthenticating
                  ? isSignUp
                    ? 'Signing up...'
                    : 'Signing in...'
                  : isSignUp
                    ? 'Sign up'
                    : 'Sign in'}
              </Button>
            </form>

            {/* Toggle Sign Up / Sign In */}
            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(!isSignUp)
                  setError(null)
                  setSuccessMessage(null)
                }}
                className="text-sm text-[#FF6B00] hover:text-[#E55A00] font-medium transition-colors duration-200 disabled:opacity-50"
                disabled={isAuthenticating}
              >
                {isSignUp
                  ? 'Already have an account? Sign in'
                  : "Don't have an account? Sign up"}
              </button>
            </div>
          </div>

          {/* Messages */}
          {successMessage && (
            <div className="mt-6 p-4 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200/60 rounded-xl shadow-sm animate-in fade-in slide-in-from-top-2 duration-300">
              <p className="text-sm text-orange-800 break-words font-medium">
                {successMessage}
              </p>
            </div>
          )}

          {error && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200/60 rounded-xl shadow-sm animate-in fade-in slide-in-from-top-2 duration-300">
              <p className="text-sm text-red-700 break-words font-medium">
                {error}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
